import { FormLogo } from '@/components/form-logo';
import { PageRequest } from '@/route-handlers/page-request';
import { isValidEmail } from '@/utils/validation/email';
import { ICONS } from '@ui/icons/types';
import BasicInput from '@ui/text-input/basic-input';
import { Validation } from '@ui/text-input/types';
import WaitingIndicatorCircle from '@ui/waiting-indicator-circle/waiting-indicator-circle';
import React, { useCallback, useState } from 'react';
import { Form, useActionData, useLoaderData } from 'react-router';

const SignIn: React.FC<unknown> = () => {
  const loaderData = useLoaderData() as { params: PageRequest };
  const actionData = useActionData() as { error?: string } | undefined;
  const signInFailure = actionData?.error;
  const [email, setEmail] = useState(loaderData.params.email ?? '');
  const [emailValid, setEmailValid] = useState(true);
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  function handleEmail(e: React.ChangeEvent<HTMLInputElement>): void {
    setEmail(e.target.value);
  }

  function handleEmailValidation(): void {
    const valid = isValidEmail(email);
    setEmailValid(valid);
    setError(valid ? '' : 'Please enter a valid email.');
  }

  const isEmailInputValid = useCallback(() => {
    // Because useState/emailValid is async, we can't use this validation on
    // form submit unless we can use something synchronous. This is mainly
    // for handling autofills that have unknown entry methods.
    return isValidEmail(email);
  }, [email]);

  const handleSubmitEmail = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      // Check if email is valid on submission to handle autofilled fields
      const emailInputIsValid = isEmailInputValid();

      if (!emailInputIsValid) {
        e.preventDefault();
        return setError('Please enter a valid email.');
      } else {
        setError('');
        setSigningIn(true);
      }
    },
    [isEmailInputValid]
  );

  const hasError = error || signInFailure || (email && !emailValid);

  return (
    <Form method="post" role="email-form" onSubmit={handleSubmitEmail} noValidate>
      <FormLogo />

      <div>
        <BasicInput
          icon={ICONS.EMAIL}
          placeholder="Email Address"
          onChange={handleEmail}
          value={email}
          onBlur={handleEmailValidation}
          valid={hasError ? Validation.error : Validation.normal}
          id="business-email"
          type="email"
          autoComplete="username"
          role="email-input"
          name="email"
          errorMessage={error || signInFailure}
        />

        <input type="hidden" name="channel" value={loaderData.params.channel || undefined} />
      </div>

      <button className={'primary'}>{signingIn ? <WaitingIndicatorCircle /> : 'Login'}</button>
    </Form>
  );
};

export default SignIn;
